import { PHYSICS_CONFIG } from "../simulation/physicsConfig.js";
/** Product-level comparison boundary for verified time-trial results. */
export const ACTIVE_RULESET_ID = "time-trial";
export const CAR_CONFIGS = Object.freeze({
    1: Object.freeze({
        id: 1,
        accel: 0.28,
        maxSpeed: 11.5,
        maxTurnRadius: 160,
        minTurnRadius: 25,
        maxTorque: 0.62,
        brake: 0.25,
        mass: 1,
        yawInertia: 72,
        wheelbase: 42,
        comToFrontAxle: 21,
        comToRearAxle: 21,
        trackWidth: 20,
        bodyLength: 56,
        maximumSteeringAngle: 0.7,
        steeringResponseRate: 0.16,
        steeringSpeedSensitivity: 0.6,
        engineForce: 0.28,
        reverseForceRatio: 0.5,
        brakeForce: 0.25,
        aerodynamicDrag: 0.002,
        rollingDrag: 0.012,
        frontCorneringStiffness: 3.2,
        rearCorneringStiffness: 3.5,
        maximumTireForce: 2.7,
        lowSpeedBlendStart: 0.6,
        lowSpeedBlendEnd: 3,
        ...PHYSICS_CONFIG,
    }),
    2: Object.freeze({
        id: 2,
        accel: 0.43,
        maxSpeed: 10.7,
        maxTurnRadius: 190,
        minTurnRadius: 35,
        maxTorque: 0.7,
        brake: 0.25,
        mass: 1.05,
        yawInertia: 78,
        wheelbase: 46,
        comToFrontAxle: 23,
        comToRearAxle: 23,
        trackWidth: 21,
        bodyLength: 60,
        maximumSteeringAngle: 0.66,
        steeringResponseRate: 0.15,
        steeringSpeedSensitivity: 0.62,
        engineForce: 0.43,
        reverseForceRatio: 0.5,
        brakeForce: 0.25,
        aerodynamicDrag: 0.0022,
        rollingDrag: 0.013,
        frontCorneringStiffness: 3.4,
        rearCorneringStiffness: 3.7,
        maximumTireForce: 2.9,
        lowSpeedBlendStart: 0.6,
        lowSpeedBlendEnd: 3.2,
        ...PHYSICS_CONFIG,
    }),
    3: Object.freeze({
        id: 3,
        accel: 0.32,
        maxSpeed: 12.1,
        maxTurnRadius: 170,
        minTurnRadius: 28,
        maxTorque: 0.5,
        brake: 0.25,
        mass: 0.98,
        yawInertia: 70,
        wheelbase: 44,
        comToFrontAxle: 22,
        comToRearAxle: 22,
        trackWidth: 20,
        bodyLength: 58,
        maximumSteeringAngle: 0.68,
        steeringResponseRate: 0.17,
        steeringSpeedSensitivity: 0.6,
        engineForce: 0.32,
        reverseForceRatio: 0.5,
        brakeForce: 0.25,
        aerodynamicDrag: 0.0018,
        rollingDrag: 0.011,
        frontCorneringStiffness: 3.1,
        rearCorneringStiffness: 3.3,
        maximumTireForce: 2.6,
        lowSpeedBlendStart: 0.6,
        lowSpeedBlendEnd: 3,
        ...PHYSICS_CONFIG,
    }),
});
export const TRACK_CONFIGS = Object.freeze({
    1: Object.freeze({
        id: 1,
        startPointX: 320,
        startPointY: 840,
        startPointRotation: 90,
        drag: 0.007,
        torque: 0.4,
        trackMaxSpeed: 0.8,
        gravelDrag: 0.1,
        gravelTorque: 4,
        gravelMaxSpeed: 0.4,
        grassDrag: 0.05,
        grassTorque: 0.08,
        grassMaxSpeed: 1,
        pitSpeedLimitRatio: 0.5,
        pitLimiterDeceleration: 0.12,
    }),
    2: Object.freeze({
        id: 2,
        startPointX: 155,
        startPointY: 1605,
        startPointRotation: 0,
        drag: 0.005,
        torque: 0.5,
        trackMaxSpeed: 1,
        gravelDrag: 0.1,
        gravelTorque: 5,
        gravelMaxSpeed: 0.4,
        grassDrag: 0.05,
        grassTorque: 0.08,
        grassMaxSpeed: 1,
        pitSpeedLimitRatio: 0.5,
        pitLimiterDeceleration: 0.12,
    }),
    3: Object.freeze({
        id: 3,
        startPointX: 3000,
        startPointY: 2250,
        startPointRotation: 270,
        drag: 0.008,
        torque: 0.5,
        trackMaxSpeed: 1.1,
        gravelDrag: 0.1,
        gravelTorque: 5,
        gravelMaxSpeed: 0.4,
        grassDrag: 0.05,
        grassTorque: 0.08,
        grassMaxSpeed: 1,
        pitSpeedLimitRatio: 0.5,
        pitLimiterDeceleration: 0.12,
    }),
    4: Object.freeze({
        id: 4,
        startPointX: 2550,
        startPointY: 1100,
        startPointRotation: 180,
        drag: 0.005,
        torque: 0.5,
        trackMaxSpeed: 0.8,
        gravelDrag: 0.1,
        gravelTorque: 5,
        gravelMaxSpeed: 0.4,
        grassDrag: 0.05,
        grassTorque: 0.08,
        grassMaxSpeed: 1,
        pitSpeedLimitRatio: 0.5,
        pitLimiterDeceleration: 0.12,
    }),
});
const PHYSICS_KEYS = [
    "id",
    "accel",
    "maxSpeed",
    "maxTurnRadius",
    "minTurnRadius",
    "maxTorque",
    "brake",
    "mass",
    "yawInertia",
    "wheelbase",
    "comToFrontAxle",
    "comToRearAxle",
    "trackWidth",
    "bodyLength",
    "maximumSteeringAngle",
    "steeringResponseRate",
    "steeringSpeedSensitivity",
    "engineForce",
    "reverseForceRatio",
    "brakeForce",
    "aerodynamicDrag",
    "rollingDrag",
    "frontCorneringStiffness",
    "rearCorneringStiffness",
    "maximumTireForce",
    "lowSpeedBlendStart",
    "lowSpeedBlendEnd",
    "grassWearRate",
    "gravelWearRate",
    "brakeWearRate",
    "wearCurve",
    "tireCriticalHealth",
    "minimumLateralGrip",
    "criticalGripPenalty",
    "tireTractionLoss",
    "tireSteeringLoss",
    "corneringWearRate",
    "tireCorneringStiffnessLoss",
    "tireSlideGripLoss",
    "asymmetricCorneringLoss",
    "grassLateralGrip",
    "grassPowerGripLoss",
    "wallRestitution",
    "wallMinimumReboundSpeed",
    "wallMaximumReboundSpeed",
    "wallSeparationDistance",
    "wallYawRetention",
];
export function canonicalPhysicsConfig(config) {
    return Object.fromEntries(PHYSICS_KEYS.map((key) => [key, config[key]]));
}
export function hashPhysicsConfig(config) {
    const value = JSON.stringify(canonicalPhysicsConfig(config));
    let hash = 0x811c9dc5;
    for (let index = 0; index < value.length; index += 1) {
        hash ^= value.charCodeAt(index);
        hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(16).padStart(8, "0");
}
